/* eslint-disable no-restricted-syntax */
/* eslint-disable radix */
/* eslint-disable prefer-const */
import moment from 'moment';
import { useMemo } from 'react';
import { precisionRenderables } from 'shared/constants';

export const retrievePrecisionNumber = (nutrient, context) => {
  switch (context) {
    case 'comparativo':
      return nutrient === 'Mo'
        ? 4
        : precisionRenderables.concat('B', 'Mg').includes(nutrient)
        ? 3
        : 2;
    default:
      return nutrient === 'Mo'
        ? 4
        : precisionRenderables.includes(nutrient)
        ? 3
        : 2;
  }
};

export const findEarliestAvailableID = async (db, collection) => {
  const existingIds = (await db[collection].toArray()).map((x) =>
    Math.max(x.id || 0, x._id || 0),
  );
  return Math.max(...(existingIds.length > 0 ? existingIds : [10000])) + 1;
};

export const noHTML = (string, replace = '') =>
  string.replace(/(<.*>)+({.*})+(<.*>)/g, replace);

/**
 * Receives an array and a filter function,
 * and returns a new array containing only elements
 * that did not return true to the filter function.
 * @param {Array} array
 * @param {Function} filter
 */
export const noRepeatingElements = (array, filter) => {
  const res = [];
  for (let element of array) {
    if (!res.some((x) => filter(x, element))) {
      res.push(element);
    }
  }
  return res;
};

/**
 * Gets classNames and returns a string of them separated by spaces.
 * If a classname is an array, evaluates its first value as boolean, and only
 * inserts the second value as a className if the boolean value is true.
 * @param  {...any} Classes
 */
export const C = (...Classes) =>
  Classes.map((x) => (x instanceof Array ? (x[0] ? x[1] : '') : x)).join(' ');

/**
 * Gets an array of numbers and returns the sum of all of them.
 * If mod is a function and is defined, applies it to the array before returning.
 * @param {Number[]} array
 * @param {() => number} mod
 */
export const Sum = (array, mod = (x) => x) => {
  let res = 0;
  for (let i = 0; i < array.length; i++) {
    res += Number(array[i]) || 0;
  }
  return mod(res);
};

/**
 * Returns an array containing <amount> <val>s.
 * @param {any} val
 * @param {number} amount
 */
export const A = (val, amount) => {
  const res = [];
  for (let i = 0; i < amount; i++) {
    res.push(val);
  }
  return res;
};

/**
 * Gets an array of keys, an array of values and returns them paired into a new object.
 * Should they not share a same length, the fallback value will be used in a missing spot instead.
 * @param {Array} keys
 * @param {Array} values
 * @param {any} fallbackVal
 */
export const O = (keys, values = [], fallbackVal = {}) => {
  const res = {};
  for (let i = 0; i < keys.length; i++) {
    res[keys[i]] =
      values[i] || values[i] === '' || values[i] === 0
        ? values[i]
        : fallbackVal instanceof Array
        ? fallbackVal[i]
        : fallbackVal;
  }
  return res;
};

/**
 * Receives and returns val if it's not undefined, a fallback value otheriwse.
 * @param {any} val
 * @param {any} fallback
 * @param  {...any} args
 */
export const T = (val, fallback = {}, ...args) => {
  try {
    if (typeof val !== 'function') {
      return typeof val !== 'undefined' ? val : fallback;
    }
    return val(...args) ? val(...args) : fallback;
  } catch (oof) {
    return fallback;
  }
};

/**
 * Gets an array containing n sublayers of arrays,
 * and brings all sublayers to root level,
 * flattening the array into a plane.
 *
 * If duplicate detector is provided, the function will run over each element
 * before insertion. If it returns true, the element is not included in the
 * resulting array.
 * @param {array} array
 * @param [function] duplicateDetector
 */
export const Flatten = (
  array,
  duplicateDetector = () => false,
  duplicateAffector = (dup) => dup,
) => {
  let res = [];
  let clone = Array.from(array);
  while (clone.length) {
    let pointer = clone[0];
    while (pointer[0] instanceof Array) {
      pointer = pointer[0];
    }
    for (let j = 0; j < pointer.length; j++) {
      if (!duplicateDetector(res, pointer[j])) {
        res.push(pointer[j]);
      } else {
        duplicateAffector(duplicateDetector(res, pointer[j]), pointer[j]);
      }
    }
    clone.splice(0, 1);
  }
  return res;
};

/**
 * Boils an array of elements down to an object {},
 * by reducing every element in the array to a single key:value pair.
 * The rules of reduction are to be established by a function.
 *
 * @param {Array} array The subject
 * @param {Function} func The reducer
 */
export const Obliterate = (array, func = (x) => x) => {
  const res = {};
  for (let i = 0; i < array.length; i++) {
    let [key, value] = func(array[i]);
    key = String(key);
    if (String(key).includes('[')) {
      const fkey = key.substring(0, key.indexOf('['));
      const indexes = key
        .replace(fkey, '')
        .split('[')
        .map((x) => x.replace(']', ''));
      if (!res[fkey]) {
        res[fkey] = {};
      }
      let pointer = res[fkey];
      for (let j = 0; j < indexes.length; j++) {
        if (indexes[j] || indexes[j] === 0) {
          if (!pointer[indexes[j]] && j < indexes.length) {
            pointer[indexes[j]] = j + 1 === indexes.length ? value : {};
          }
          if (j < indexes.length) {
            pointer = pointer[indexes[j]];
          } else {
            pointer[indexes[j]] = value;
          }
        }
      }
    } else {
      res[key] = value;
    }
  }
  return res;
};

const _formatterInstances = {};
function getNumberFormatterInstance(fracMin = 0, fracMax = 0) {
  if (!_formatterInstances[`${fracMin}${fracMax}`]) {
    _formatterInstances[`${fracMin}${fracMax}`] = new Intl.NumberFormat(
      'pt-BR',
      {
        minimumFractionDigits: fracMin,
        maximumFractionDigits: fracMax,
      },
    );
  }
  return _formatterInstances[`${fracMin}${fracMax}`];
}

const _currencyFormatterInstances = {};
function getCurrencyFormatterInstance(fracMin = 0, fracMax = 0) {
  if (!_currencyFormatterInstances[`${fracMin}${fracMax}`]) {
    _currencyFormatterInstances[`${fracMin}${fracMax}`] = new Intl.NumberFormat(
      'pt-BR',
      {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: fracMin,
        maximumFractionDigits: fracMax,
        ed: undefined,
      },
    );
  }
  return _currencyFormatterInstances[`${fracMin}${fracMax}`];
}

export const Masks = {
  currency: (
    value,
    { suffix, prefix, mind, maxd, ed } = {
      mind: 2,
      suffix: '',
      prefix: '',
      maxd: 2,
      ed: undefined,
    },
  ) =>
    (prefix || '') +
    getCurrencyFormatterInstance(
      ed === null || ed === undefined ? ed || mind || 2 : ed,
      ed === null || ed === undefined ? ed || maxd || 2 : ed,
    ).format(value instanceof Array ? Sum(value) : value || 0) +
    (suffix || ''),
  number: (
    value,
    { suffix, prefix, mind, maxd, ed } = {
      mind: 2,
      suffix: '',
      prefix: '',
      maxd: 2,
      ed: undefined,
    },
  ) => {
    if (Number.isNaN(Number(value)) && !(value instanceof Array)) {
      value = 0;
    }
    return (
      (prefix || '') +
      getNumberFormatterInstance(
        ed === undefined ? ed || mind || 2 : ed,
        ed === undefined ? ed || maxd || 2 : ed,
      ).format(
        value instanceof Array
          ? Sum(value) !== Infinity && !Number.isNaN(Sum(value))
            ? Sum(value)
            : 0
          : value !== Infinity && !Number.isNaN(value)
          ? value === 0
            ? 0
            : value
          : 0,
      ) +
      (suffix
        ? [' ', '/', '%'].includes(suffix.charAt(0))
          ? suffix
          : ` ${suffix}`
        : '')
    );
  },
};

export default class Helpers {
  static constructModel(data, context) {
    Object.keys(data).forEach((key) => {
      if (key === 'createdAt') {
        context.createdAt = data.createdAt
          ? data.createdAt
          : moment().toDate().toISOString();
      } else {
        context[key] = data[key];
      }
    });
  }

  static responseError(error) {
    let msg = 'Serviço indisponível, tente novamente mais tarde';
    if (error.response) {
      if (error.response.data.message) msg = error.response.data.message;
      if (error.response.data.error) {
        msg =
          typeof error.response.data.error === 'string'
            ? error.response.data.error
            : error.response.data.error instanceof Array
            ? error.response.data.error[0]
            : Object.values(error.response.data.error)[0];
      }
    }
    return msg;
  }

  static async asyncForEach(array, callback, options = {}) {
    const {
      exhaustArray = false,
      forceControl = false,
      done = () => {},
    } = options;

    const control = {
      interrupt: false,
      array: [...array],
      index: 0,
      lock: false,
    };

    if (!exhaustArray) {
      for (
        control.index;
        control.index < control.array.length;
        control.index++
      ) {
        if (!control.interrupt) {
          if (forceControl) {
            await callback(control);
          } else {
            await callback(
              control.array[control.index],
              control.index,
              control.array,
              control,
            );
          }
        } else {
          break;
        }
      }
    } else {
      while (control.array.length) {
        if (!control.lock) {
          control.lock = true;
          const modifier = await callback(control);
          if (modifier) {
            control.array = modifier(control.array);
            control.index++;
          }
          control.lock = false;
        }
      }
      await done();
    }
    return true;
  }
}

export const FindPerguntaByCode = (perguntas = [], code, fallback = {}) => {
  const pergunta = perguntas.find((p) => p && p.code === code);
  if (pergunta) {
    return pergunta;
  }
  return fallback;
};

export function useRespostaByPerguntaCode(Perguntas, Respostas, perguntaCode) {
  return useMemo(() => {
    const perguntaId = T(FindPerguntaByCode(Perguntas, perguntaCode).id, '');
    const resposta = T(
      Respostas.find(
        (resposta) => String(perguntaId) === String(resposta.pergunta_id),
      ),
      {},
    ).resposta;
    return resposta;
  }, [perguntaCode, Respostas, Perguntas]);
}

export function averagePriceImpactosQualidade(
  impacto,
  key,
  produtividadeConvencional,
) {
  return Sum(
    Object.values(T(T(T(impacto).porcentagem)[key], [])).map(
      (x = 0, y) =>
        (x / 100) *
        (impacto.preco[key][y] || 0) *
        (key === 'svb' ? impacto.produtividade : produtividadeConvencional),
    ),
  );
}

/**
 *
 * @param {Number} numberToRound
 * @param {Number} decimals
 */
export function roundToDecimals(numberToRound, decimals = 2) {
  return Number.parseFloat(numberToRound.toFixed(decimals));
}

/**
 * This function fits the element to an A4 page an returns a function
 * that restore it to the previous state after printing
 * @param {*} baseElement
 */
function fitElement(baseElement) {
  if (baseElement && baseElement.width) {
    const previousWidth = baseElement.width();
    baseElement.width(670);
    return () => {
      baseElement.width(previousWidth);
    };
  }
  if (baseElement && baseElement.scrollWidth > 670) {
    const scale = Math.floor((670 / baseElement.scrollWidth) * 10) / 10;
    const _class = `print-scale-${scale.toFixed(1).replace('.', '-')}`;
    if (!baseElement.classList.contains(_class)) {
      for (let i = 0; i < baseElement.length; i++) {
        const _itemClass = baseElement.classList.item(i);
        if (_itemClass.indexOf('print-scale-') !== -1) {
          baseElement.classList.remove(_itemClass);
        }
      }
      baseElement.classList.add(_class);
      baseElement.classList.add('print-scale-base');
    }
    baseElement.previousHeight = baseElement.style.height;
    baseElement.previousWidth = baseElement.style.width;
    baseElement.style.height = baseElement.scrollHeight * scale + 'px';
    baseElement.style.width = baseElement.scrollWidth * scale + 'px';
    return () => {
      baseElement.style.height = baseElement.previousHeight;
      baseElement.style.width = baseElement.previousWidth;
    };
  }
  return () => {};
}

/**
 *
 * @param {HTMLElement[]} elements Elements to be fit
 */
export function printElements(elements) {
  const callbacks = elements.map((e) => fitElement(e));
  window.print();
  callbacks.forEach((c) => (c ? c() : c));
}
